import { useCallback, useEffect, useReducer, useRef } from "react";
import {
  formatTime,
  formatTimeShort,
  getSupportedMime,
  detectMobileSafari,
  validateFileDuration,
  applyChromeDurationFix,
} from "../util/videoRecorderUtils";

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

export const ACTIONS = Object.freeze({
  STREAM_READY: "STREAM_READY",
  STREAM_ERROR: "STREAM_ERROR",
  COUNTDOWN_START: "COUNTDOWN_START",
  COUNTDOWN_TICK: "COUNTDOWN_TICK",
  RECORDING_START: "RECORDING_START",
  TIMER_TICK: "TIMER_TICK",
  RECORDING_STOP: "RECORDING_STOP",
  PREVIEW_READY: "PREVIEW_READY",
  FILE_SELECTED: "FILE_SELECTED",
  FILE_ERROR: "FILE_ERROR",
  RETRY: "RETRY",
  SET_MOBILE_SAFARI: "SET_MOBILE_SAFARI",
});

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

export const createInitialState = (config) =>
  Object.freeze({
    phase: "idle",
    countdownValue: config.countdownSeconds,
    timerSeconds:
      config.timerDirection === "down" ? config.maxDurationSeconds : 0,
    error: null,
    mimeType: "",
    isUploadedFile: false,
    isMobileSafari: false,
  });

export const reducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.STREAM_READY:
      return {
        ...state,
        phase: "idle",
        error: null,
        mimeType: action.payload.mimeType ?? state.mimeType,
      };

    case ACTIONS.STREAM_ERROR:
      return {
        ...state,
        phase: "error",
        error: action.payload.error,
      };

    case ACTIONS.COUNTDOWN_START:
      return {
        ...state,
        phase: "countdown",
        countdownValue: action.payload.countdownSeconds,
        error: null,
      };

    case ACTIONS.COUNTDOWN_TICK:
      return {
        ...state,
        countdownValue: state.countdownValue - 1,
      };

    case ACTIONS.RECORDING_START:
      return {
        ...state,
        phase: "recording",
        timerSeconds:
          action.payload.timerDirection === "down"
            ? action.payload.maxDurationSeconds
            : 0,
      };

    case ACTIONS.TIMER_TICK:
      return {
        ...state,
        timerSeconds:
          action.payload.timerDirection === "down"
            ? state.timerSeconds - 1
            : state.timerSeconds + 1,
      };

    case ACTIONS.RECORDING_STOP:
      return {
        ...state,
        phase: state.phase === "recording" ? "preview" : state.phase,
        isUploadedFile: false,
      };

    case ACTIONS.PREVIEW_READY:
      return {
        ...state,
        phase: "preview",
        isUploadedFile: action.payload.isUpload ?? false,
        mimeType: action.payload.mimeType ?? state.mimeType,
      };

    case ACTIONS.FILE_SELECTED:
      return {
        ...state,
        phase: "preview",
        isUploadedFile: true,
        mimeType: action.payload.mimeType ?? state.mimeType,
      };

    case ACTIONS.FILE_ERROR:
      return {
        ...state,
        phase: "error",
        error: action.payload.error,
      };

    case ACTIONS.RETRY:
      return {
        ...state,
        phase: "idle",
        error: null,
        timerSeconds:
          action.payload.timerDirection === "down"
            ? action.payload.maxDurationSeconds
            : 0,
        isUploadedFile: false,
      };

    case ACTIONS.SET_MOBILE_SAFARI:
      return {
        ...state,
        isMobileSafari: action.payload.value,
      };

    default:
      return state;
  }
};

// ---------------------------------------------------------------------------
// Recorder construction
// ---------------------------------------------------------------------------

// videoKeyFrameIntervalDuration is Chromium-only, and Firefox throws on an
// unrecognised option rather than ignoring it — hence the retry without it.
// Returns null when no recorder can be built at all.
const buildRecorder = (stream, options, keyFrameIntervalMs) => {
  const attempt = (opts) => {
    try {
      return new MediaRecorder(stream, opts);
    } catch {
      return null;
    }
  };

  return (
    (keyFrameIntervalMs &&
      attempt({
        ...options,
        videoKeyFrameIntervalDuration: keyFrameIntervalMs,
      })) ||
    attempt(options)
  );
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

const useVideoRecorder = (config) => {
  const {
    maxDurationSeconds,
    countdownSeconds,
    timerDirection,
    flashAtRemainingSeconds,
    autoSetupStream,
    videoBitsPerSecond,
    audioBitsPerSecond,
    videoKeyFrameIntervalMs,
    getUserMediaConstraints,
    mimePriority,
    maxFileDurationSeconds,
  } = config;

  const effectiveMaxFileDuration = maxFileDurationSeconds ?? maxDurationSeconds;

  const [state, dispatch] = useReducer(reducer, config, createInitialState);

  // Refs
  const videoRef = useRef(null);
  const fileInputRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const blobRef = useRef(null);
  const objectUrlRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const timerIntervalRef = useRef(null);
  const elapsedRef = useRef(0);
  const remainingRef = useRef(0);
  const mountedRef = useRef(true);

  // --- Internal helpers ---

  const revokeObjectUrl = useCallback(() => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  }, []);

  const clearIntervals = useCallback(() => {
    clearInterval(countdownIntervalRef.current);
    clearInterval(timerIntervalRef.current);
    countdownIntervalRef.current = null;
    timerIntervalRef.current = null;
  }, []);

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().map((track) => track.stop());
    streamRef.current = null;
  }, []);

  const showPreviewBlob = useCallback(
    (blob) => {
      revokeObjectUrl();
      const url = URL.createObjectURL(blob);
      objectUrlRef.current = url;

      if (videoRef.current) {
        videoRef.current.srcObject = null;
        videoRef.current.src = url;
        videoRef.current.muted = false;
      }
    },
    [revokeObjectUrl],
  );

  // --- Stream setup ---
  // Returns true on success, false on failure.

  const setupStream = useCallback(async () => {
    try {
      // Releasing first keeps a second call from stranding the camera on the
      // stream it replaces — getUserMedia hands back a new one either way.
      stopStream();

      const stream = await navigator.mediaDevices.getUserMedia(
        getUserMediaConstraints,
      );
      if (!mountedRef.current) {
        stream.getTracks().map((t) => t.stop());
        return false;
      }
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.muted = true;
        videoRef.current.play();
      }

      // Pre-detect supported MIME
      const mimeType =
        typeof MediaRecorder !== "undefined"
          ? getSupportedMime(mimePriority, (m) =>
              MediaRecorder.isTypeSupported(m),
            )
          : "";

      dispatch({
        type: ACTIONS.STREAM_READY,
        payload: { mimeType },
      });
      return true;
    } catch (err) {
      if (!mountedRef.current) return false;
      const message =
        err.name === "NotAllowedError"
          ? "Camera and Microphone permissions are disabled in your browser. Please enable them and try again."
          : "Could not access camera and microphone";
      dispatch({
        type: ACTIONS.STREAM_ERROR,
        payload: { error: message },
      });
      return false;
    }
  }, [getUserMediaConstraints, mimePriority, stopStream]);

  // --- Recording lifecycle ---

  const startRecording = useCallback(() => {
    if (!streamRef.current) return;

    chunksRef.current = [];
    revokeObjectUrl();
    blobRef.current = null;

    const mimeType =
      typeof MediaRecorder !== "undefined"
        ? getSupportedMime(mimePriority, (m) =>
            MediaRecorder.isTypeSupported(m),
          )
        : "";

    const recorderOptions = {
      ...(mimeType ? { mimeType } : {}),
      ...(audioBitsPerSecond ? { audioBitsPerSecond } : {}),
      ...(videoBitsPerSecond ? { videoBitsPerSecond } : {}),
    };

    const recorder = buildRecorder(
      streamRef.current,
      recorderOptions,
      videoKeyFrameIntervalMs,
    );

    if (!recorder) {
      if (!mountedRef.current) return;
      dispatch({
        type: ACTIONS.STREAM_ERROR,
        payload: {
          error:
            "Something went wrong when trying to start recording. Please try a different browser.",
        },
      });
      return;
    }

    recorder.ondataavailable = (e) => {
      if (!mountedRef.current) return;
      if (e.data.size > 0) {
        chunksRef.current = [...chunksRef.current, e.data];
      }
    };

    recorder.onstop = async () => {
      if (!mountedRef.current) return;
      const resolvedMime = mimeType || "video/webm";
      const blob = new Blob(chunksRef.current, { type: resolvedMime });
      const file = new File([blob], "recording.webm", { type: resolvedMime });
      blobRef.current = file;

      showPreviewBlob(blob);

      // Apply Chrome duration fix then show preview
      try {
        await applyChromeDurationFix(videoRef.current);
      } catch {
        // Non-critical — preview still works
      }

      if (!mountedRef.current) return;

      if (videoRef.current) {
        videoRef.current.currentTime = 0;
        videoRef.current.play();
      }

      dispatch({
        type: ACTIONS.PREVIEW_READY,
        payload: { isUpload: false, mimeType: resolvedMime },
      });
    };

    recorderRef.current = recorder;
    recorder.start(1000);

    dispatch({
      type: ACTIONS.RECORDING_START,
      payload: { timerDirection, maxDurationSeconds },
    });

    elapsedRef.current = 0;
    timerIntervalRef.current = setInterval(() => {
      if (!mountedRef.current) return;
      elapsedRef.current += 1;
      dispatch({
        type: ACTIONS.TIMER_TICK,
        payload: { timerDirection },
      });
      if (elapsedRef.current >= maxDurationSeconds) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
        if (recorderRef.current && recorderRef.current.state === "recording") {
          recorderRef.current.stop();
        }
        stopStream();
      }
    }, 1000);
  }, [
    mimePriority,
    audioBitsPerSecond,
    videoBitsPerSecond,
    videoKeyFrameIntervalMs,
    timerDirection,
    maxDurationSeconds,
    revokeObjectUrl,
    showPreviewBlob,
    stopStream,
  ]);

  // --- Countdown ---

  const startCountdown = useCallback(() => {
    dispatch({
      type: ACTIONS.COUNTDOWN_START,
      payload: { countdownSeconds },
    });

    remainingRef.current = countdownSeconds;
    countdownIntervalRef.current = setInterval(() => {
      if (!mountedRef.current) return;
      remainingRef.current -= 1;
      if (remainingRef.current <= 0) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
        startRecording();
      } else {
        dispatch({ type: ACTIONS.COUNTDOWN_TICK });
      }
    }, 1000);
  }, [countdownSeconds, startRecording]);

  // --- Stop ---

  const stopRecording = useCallback(() => {
    clearIntervals();
    if (recorderRef.current && recorderRef.current.state === "recording") {
      recorderRef.current.stop();
    }
    stopStream();
  }, [clearIntervals, stopStream]);

  // --- Retry ---

  const reset = useCallback(() => {
    clearIntervals();
    if (recorderRef.current && recorderRef.current.state === "recording") {
      recorderRef.current.stop();
    }
    revokeObjectUrl();
    blobRef.current = null;
    dispatch({
      type: ACTIONS.RETRY,
      payload: { timerDirection, maxDurationSeconds },
    });
  }, [clearIntervals, revokeObjectUrl, timerDirection, maxDurationSeconds]);

  const retry = useCallback(() => {
    reset();
    stopStream();
    setupStream();
  }, [reset, stopStream, setupStream]);

  const ensureStream = useCallback(
    async () => (streamRef.current?.active ? true : setupStream()),
    [setupStream],
  );

  // --- File upload ---

  const selectFile = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileSelect = useCallback(
    async (evt) => {
      const selected = evt.target.files?.[0];
      if (!selected) return;

      // Reset input so re-selecting same file fires onChange
      // eslint-disable-next-line no-param-reassign
      evt.target.value = "";

      try {
        await validateFileDuration(selected, {
          maxSeconds: effectiveMaxFileDuration,
        });
        stopStream();
        blobRef.current = selected;
        showPreviewBlob(selected);

        if (videoRef.current) {
          videoRef.current.play();
        }

        dispatch({
          type: ACTIONS.FILE_SELECTED,
          payload: { mimeType: selected.type },
        });
      } catch (err) {
        if (!mountedRef.current) return;
        dispatch({
          type: ACTIONS.FILE_ERROR,
          payload: { error: err.message },
        });
      }
    },
    [effectiveMaxFileDuration, stopStream, showPreviewBlob],
  );

  // --- loadBlob (for retry support — vendor modal) ---

  const loadBlob = useCallback(
    (blob, { isUpload = false, mimeType = "" } = {}) => {
      blobRef.current = blob;
      showPreviewBlob(blob);

      if (videoRef.current) {
        videoRef.current.onloadedmetadata = () => {
          if (!mountedRef.current) return;
          if (videoRef.current) {
            videoRef.current.onloadedmetadata = null;
            videoRef.current.currentTime = 0;
          }
          dispatch({
            type: ACTIONS.PREVIEW_READY,
            payload: { isUpload, mimeType },
          });
        };
      }
    },
    [showPreviewBlob],
  );

  // --- getBlob ---

  const getBlob = useCallback(() => blobRef.current, []);

  // --- Detect Mobile Safari on mount ---

  useEffect(() => {
    const detected = detectMobileSafari(navigator.userAgent);
    if (detected) {
      dispatch({
        type: ACTIONS.SET_MOBILE_SAFARI,
        payload: { value: true },
      });
    }
  }, []);

  // --- Auto-setup stream on mount ---

  useEffect(() => {
    mountedRef.current = true;
    if (autoSetupStream) {
      setupStream();
    }
    return () => {
      mountedRef.current = false;
      clearIntervals();
      if (recorderRef.current && recorderRef.current.state === "recording") {
        recorderRef.current.stop();
      }
      stopStream();
      revokeObjectUrl();
    };
    // Only run on mount/unmount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Computed values ---

  const showMirror =
    state.phase === "idle" ||
    state.phase === "countdown" ||
    state.phase === "recording";

  const timerDisplay =
    timerDirection === "down"
      ? formatTimeShort(state.timerSeconds)
      : `${formatTime(state.timerSeconds)} / ${formatTime(maxDurationSeconds)}`;

  const remainingSeconds =
    timerDirection === "up"
      ? maxDurationSeconds - state.timerSeconds
      : state.timerSeconds;

  const isFlashing =
    flashAtRemainingSeconds !== null &&
    state.phase === "recording" &&
    remainingSeconds <= flashAtRemainingSeconds;

  return {
    state,
    videoRef,
    fileInputRef,
    setupStream,
    startCountdown,
    stopRecording,
    reset,
    retry,
    ensureStream,
    selectFile,
    handleFileSelect,
    getBlob,
    loadBlob,
    showMirror,
    timerDisplay,
    isFlashing,
  };
};

export default useVideoRecorder;
